import React, { useEffect } from 'react';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import getFormStateHandler from 'services/forms/getFormStateHandler';
import getSellingPricePerUnit from 'services/products/calculations/getSellingPricePerUnit';
import LinkButton from 'components/layout/buttons/LinkButton';
import NumberWithDropdownInputField from 'components/layout/forms/NumberWithDropdownInputField';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import TextInputField from 'components/layout/forms/TextInputField';
import translations from 'services/translations/translations';
import { fetchGroups, fetchUnits } from 'store/products/actions';

import ActionButtons from './ActionButtons';
import getProductGroupOptions from './getProductGroupOptions';
import getProductUnitOptions from './getProductUnitOptions';
import IFormState from './IFormState';
import { isPositionInUse } from './usedPositions';
import VersionsSummaryTable from './VersionsSummaryTable';

const ProductFields = ({
  cancel,
  formState,
  locked,
  openCalculationWindow,
  openPreview,
  remove,
  save,
  setFormState,
  usedPositions,
}: {
  cancel: () => void;
  formState: IFormState;
  locked: boolean;
  openCalculationWindow: () => void;
  // Opens the PDF preview of the offer
  openPreview?: () => void;
  remove?: () => void;
  save: (preventRedirect: boolean) => void;
  setFormState: (formState: IFormState) => void;
  // The position codes of the other positions of the offer
  usedPositions?: string[];
}): JSX.Element | null => {
  const { t } = useTranslation([
    translations.common,
    translations.offers,
    translations.productConfigurations,
    translations.products,
  ]);

  const dispatch = useDispatch();
  const groups = useSelector((state: any) => state.products.groups);
  const units = useSelector((state: any) => state.products.units);

  const handleFormState = getFormStateHandler(formState, setFormState);

  useEffect(() => {
    dispatch(fetchGroups());
    dispatch(fetchUnits());
  }, []);

  const primaryVersion = formState.versions.find(
    (version) => version.isPrimary
  );

  if (primaryVersion === undefined) {
    return null;
  }

  const sellingPricePerUnit = getSellingPricePerUnit(
    primaryVersion.installations,
    primaryVersion.subproducts
  );

  const offerIsLocked = () => locked;

  // A row of the additional price list: no position code and no alternatives
  const priceList = formState.isPriceListItem === true;

  const priceUnit =
    formState.unit === ''
      ? '€'
      : `€/${t(`${translations.common}:units:${formState.unit}`)}`;

  return (
    <>
      <Row>
        <Col>
          <SelectInputFieldControlled
            id="product-group"
            label={t(`${translations.products}:Product Group`)}
            name="productGroup"
            onChange={handleFormState}
            options={getProductGroupOptions(groups, t)}
            required
            value={formState.productGroup}
            disabled={offerIsLocked()}
          />
        </Col>
        <Col>
          <TextInputField
            id="name"
            label={t(`${translations.products}:Product`)}
            name="name"
            onChange={handleFormState}
            required
            value={formState.name}
            disabled={offerIsLocked()}
          />
        </Col>
      </Row>
      <Row>
        <Col sm="6">
          <NumberWithDropdownInputField
            disabled={offerIsLocked()}
            dropdownName="unit"
            dropdownOnChange={handleFormState}
            dropdownOptions={getProductUnitOptions(units, t)}
            dropdownValue={formState.unit}
            id="quantity-and-unit"
            label={t(`${translations.common}:Quantity`)}
            numberName="quantity"
            numberOnChange={handleFormState}
            numberValue={formState.quantity}
            required
          />
        </Col>
        {priceList ? (
          <Col sm="3">
            {/* Printed instead of the calculated price, e.g. "25 %"; without a calculation
                and a price text the row is a note or a subheading of the price list */}
            <TextInputField
              id="price-text"
              label={t(`${translations.offers}:priceList.Price text`)}
              name="priceText"
              onChange={handleFormState}
              value={formState.priceText ?? ''}
              disabled={offerIsLocked()}
            />
          </Col>
        ) : (
          <Col sm="3">
            <TextInputField
              id="position"
              invalidMessage={
                isPositionInUse(formState.position, usedPositions ?? [])
                  ? t(`${translations.offers}:Position in use`, {
                      position: formState.position.trim(),
                    })
                  : undefined
              }
              label={t(`${translations.products}:Position`)}
              name="position"
              onChange={handleFormState}
              required
              value={formState.position}
              disabled={offerIsLocked()}
            />
          </Col>
        )}
        <Col className="price-cell" sm="3">
          <NumberWithUnitInputField
            disabled
            id="price"
            label={t(`${translations.common}:Price`)}
            name="price"
            unit={priceUnit}
            value={formatGroupedNumber(sellingPricePerUnit)}
          />
          <LinkButton onClick={openCalculationWindow}>
            {t(`${translations.offers}:Calculation window`)}
          </LinkButton>
        </Col>
      </Row>
      {priceList ? null : (
        <Row>
          <Col>
            <VersionsSummaryTable
              formState={formState}
              locked={offerIsLocked()}
              setFormState={setFormState}
            />
          </Col>
        </Row>
      )}
      <ActionButtons
        locked={locked}
        cancel={cancel}
        openPreview={openPreview}
        remove={remove}
        save={() => save(false)}
        saveButtonText={t(`${translations.common}:Save`)}
      />
    </>
  );
};

export default ProductFields;
